import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Ticket, Plus, Edit, Trash2, X, Search, Calendar, DollarSign } from 'lucide-react';
import { couponsAPI } from '../../services/api';

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'fixed',
    value: '',
    max_usage: '',
    valid_from: '',
    valid_to: '',
    applies_to_all_labs: true,
    active: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setIsLoading(true);
      const response = await couponsAPI.getAll();
      setCoupons(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('حدث خطأ أثناء جلب البيانات');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setCurrentCoupon(null);
    setFormData({
      name: '',
      code: '',
      type: 'fixed',
      value: '',
      max_usage: '',
      valid_from: new Date().toISOString().split('T')[0],
      valid_to: '',
      applies_to_all_labs: true,
      active: true
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (coupon) => {
    setIsEditing(true);
    setCurrentCoupon(coupon);
    setFormData({
      name: coupon.name,
      code: coupon.code,
      type: coupon.type,
      value: coupon.value,
      max_usage: coupon.max_usage || '',
      valid_from: new Date(coupon.valid_from).toISOString().split('T')[0],
      valid_to: new Date(coupon.valid_to).toISOString().split('T')[0],
      applies_to_all_labs: coupon.applies_to_all_labs,
      active: coupon.active
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const dataToSend = {
        ...formData,
        value: parseFloat(formData.value),
        max_usage: formData.max_usage ? parseInt(formData.max_usage) : null
      };

      if (isEditing) {
        await couponsAPI.update(currentCoupon._id, dataToSend);
        toast.success('تم تحديث الكوبون بنجاح');
      } else {
        await couponsAPI.create(dataToSend);
        toast.success('تم إضافة الكوبون بنجاح');
      }
      
      setShowModal(false);
      fetchCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      toast.error(error.response?.data?.message || 'حدث خطأ أثناء حفظ البيانات');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الكوبون؟')) return;
    
    try {
      await couponsAPI.delete(id);
      toast.success('تم حذف الكوبون بنجاح');
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('حدث خطأ أثناء حذف الكوبون');
    }
  };

  const filteredCoupons = coupons.filter(coupon => 
    coupon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coupon.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getUsagePercentage = (coupon) => {
    if (!coupon.max_usage) return 0;
    return (coupon.usage_count / coupon.max_usage) * 100;
  };

  const getUsageText = (coupon) => {
    if (!coupon.max_usage) return '∞';
    return `${coupon.usage_count}/${coupon.max_usage}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
            <Ticket className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
            <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">أكواد الخصم</h1>
            <p className="text-slate-500 text-sm mt-1">إدارة كوبونات الخصم والعروض الترويجية</p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleOpenAddModal}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          <span>إضافة كوبون جديد</span>
        </motion.button>
      </motion.div>

      {/* Search Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="بحث عن كوبون..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-12 pl-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Coupons Table */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">حالة</th>
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الاسم</th>
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الكود</th>
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">النوع</th>
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">القيمة</th>
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الاستخدامات</th>
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">صالح حتى</th>
                <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filteredCoupons.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Ticket className="w-12 h-12 text-slate-300" strokeWidth={1.5} />
                      <p className="text-slate-500">لا توجد كوبونات</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCoupons.map((coupon, index) => (
                  <motion.tr 
                    key={coupon._id} 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                  >
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide border ${
                        coupon.active 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${coupon.active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        {coupon.active ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="font-semibold text-slate-900 text-sm">{coupon.name}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-mono font-semibold border border-blue-200">
                        {coupon.code}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700">{coupon.type === 'fixed' ? 'ثابتة' : 'نسبة'}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="font-bold text-slate-900 text-sm tabular-nums">
                      {coupon.type === 'fixed' ? `${coupon.value} ر.س` : `${coupon.value}%`}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden max-w-[100px]">
                          <div 
                            className="h-full bg-blue-600 transition-all"
                            style={{ width: `${Math.min(getUsagePercentage(coupon), 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-600 font-medium">
                          {getUsageText(coupon)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700">{new Date(coupon.valid_to).toLocaleDateString('en-GB')}</span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleOpenEditModal(coupon)}
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon._id)}
                          className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {isEditing ? 'تعديل الكوبون' : 'إضافة كوبون جديد'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  معلومات الكوبون
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الاسم <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="اسم الكوبون"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الكود <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      required
                      placeholder="كود الكوبون"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                    />
                    <p className="text-xs text-gray-500 mt-1">أدخل كوداً فريداً لهذا الكوبون</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    النوع <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="fixed">مبلغ ثابت</option>
                    <option value="percentage">نسبة مئوية</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    القيمة <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="value"
                      value={formData.value}
                      onChange={handleChange}
                      required
                      step="0.01"
                      min="0"
                      placeholder="قيمة الخصم"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {formData.type === 'fixed' ? (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">ر.س</span>
                    ) : (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">%</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  حد الاستخدام
                </label>
                <input
                  type="number"
                  name="max_usage"
                  value={formData.max_usage}
                  onChange={handleChange}
                  min="1"
                  placeholder="عدد مرات استخدام الكوبون"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">اتركه فارغاً لاستخدام غير محدود</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    نشط من <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="valid_from"
                      value={formData.valid_from}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    نشط حتى <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="valid_to"
                      value={formData.valid_to}
                      onChange={handleChange}
                      required
                      min={formData.valid_from}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6 p-4 bg-blue-50 rounded-xl">
                <input
                  type="checkbox"
                  id="applies_to_all_labs"
                  name="applies_to_all_labs"
                  checked={formData.applies_to_all_labs}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="applies_to_all_labs" className="text-sm text-gray-700 font-medium cursor-pointer">
                  جعل هذا الكوبون متاحاً لجميع المختبرات في الشبكة
                </label>
              </div>

              <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="active" className="text-sm text-gray-700 font-medium cursor-pointer">
                  تفعيل الكوبون
                </label>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold"
                >
                  {isEditing ? 'تحديث' : 'حفظ'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Coupons;

